import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import API_ENDPOINTS, { withBackendPath } from '../config/api';
import { hasRole } from '../utils/roles';
import { getRequiredRole, mayOpenPath, grantOpensPath, FACILITATOR_HOME } from './AdminHeader/adminNav';
import { checkPinSession, clearPinSession, getPinInstanceId, isPinSessionValid, markPinSession } from '../utils/pinSession';
import { workshopAccess } from '../utils/workshopAccess';
import { readRememberedInstanceId } from '../hooks/useAdminInstance';
import { canOnInstance } from '../utils/access';

const PIN_SESSION_CHECK_MS = 5 * 60 * 1000;

/**
 * AuthContext - Shared auth state for the entire app
 *
 * Shape:
 * - isAuthenticated: boolean - Has valid auth
 * - isLocked: boolean - Device in locked mode
 * - isAdmin: boolean - isAuthenticated && !isLocked
 * - login: (pin) => Promise<boolean>
 * - logout: () => void
 * - lock: () => void
 * - unlock: (pin) => Promise<boolean>
 */
const AuthContext = createContext({
  isAuthenticated: false,
  isLocked: false,
  isAdmin: false,
  isParticipant: false,
  participantInstanceId: null,
  // Entra-authenticated user (server session via /auth/me), null when signed out.
  user: null,
  login: async () => false,
  logout: () => {},
  lock: () => {},
  unlock: async () => false,
});

export const useAuth = () => useContext(AuthContext);

/**
 * AuthGate - The authentication wall around the app
 *
 * Not authenticated → Shows PIN login
 * Authenticated → Shows children
 * Locked mode → Shows children but isAdmin=false
 *
 * Usage:
 * <AuthGate>
 *   <App />
 * </AuthGate>
 */
const AuthGate = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    if (!isPinSessionValid()) {
      clearPinSession();
      return false;
    }
    // Every PIN login is an instance login: the admin PIN was retired with the
    // access model (#793, specs/access-model.md), so kuutio-admin-auth is gone.
    return localStorage.getItem('kuutio-participant-auth') === 'true';
  });
  const [isLocked, setIsLocked] = useState(() =>
    isPinSessionValid() && localStorage.getItem('kuutio-device-locked') === 'true'
  );
  const [isParticipant, setIsParticipant] = useState(() =>
    isPinSessionValid() && localStorage.getItem('kuutio-participant-auth') === 'true'
  );
  const [participantInstanceId, setParticipantInstanceId] = useState(() =>
    isPinSessionValid() ? (localStorage.getItem('kuutio-participant-instance') || null) : null
  );

  // Entra session (server-validated via /auth/me).
  //   undefined → still loading (don't gate yet)
  //   null      → not signed in
  //   object    → { id, email, name, role, grants }
  const [me, setMe] = useState(undefined);

  useEffect(() => {
    let cancelled = false;
    // Staff sessions are set by the backend's own site (Entra callback);
    // participant sessions by the app's site (the /me door, proxied by
    // Vercel). Ask both, take whichever knows us. One site once both are
    // first-party (specs/auth-same-origin-migration.md).
    const ask = (url) => fetch(url, { credentials: 'include' }).then(r => (r.ok ? r.json() : null)).catch(() => null);
    const backend = withBackendPath('/auth/me');
    Promise.all([ask(backend), backend === '/auth/me' ? Promise.resolve(null) : ask('/auth/me')])
      .then(([a, b]) => { if (!cancelled) setMe(a || b || null); });
    return () => { cancelled = true; };
  }, []);

  // Sync with localStorage changes (other tabs)
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === 'kuutio-participant-auth') {
        setIsAuthenticated(e.newValue === 'true');
      }
      if (e.key === 'kuutio-device-locked') {
        setIsLocked(e.newValue === 'true');
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  // Login with PIN
  const login = useCallback(async (pin) => {
    try {
      const response = await fetch(API_ENDPOINTS.AUTH_PIN_LOGIN(), {
        credentials: 'include',
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pinCode: pin }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Always an instance PIN: the session carries the Kuutio it opens.
        markPinSession();
        const instanceId = data.session?.kuutioInstanceId || null;
        localStorage.setItem('kuutio-participant-auth', 'true');
        if (instanceId) localStorage.setItem('kuutio-participant-instance', instanceId);
        localStorage.removeItem('kuutio-device-locked');
        setIsAuthenticated(true);
        setIsLocked(false);
        setIsParticipant(true);
        setParticipantInstanceId(instanceId);
        return true;
      }
      return false;
    } catch (err) {
      console.error('[AuthGate] Login error:', err);
      return false;
    }
  }, []);

  // Logout - clear all auth: the server session (both sites, like /auth/me
  // asks both) and the device's own PIN state. setMe(null) is what actually
  // closes the gate for an Entra user; without it, signing out only cleared
  // PIN state and the button did nothing (#793 testing).
  const logout = useCallback(() => {
    const tell = (url) => fetch(url, { method: 'POST', credentials: 'include' }).catch(() => {});
    const backend = withBackendPath('/auth/logout');
    tell(backend);
    if (backend !== '/auth/logout') tell('/auth/logout');
    setMe(null);
    clearPinSession();
    localStorage.removeItem('kuutio-admin-instance');
    setIsAuthenticated(false);
    setIsLocked(false);
    setIsParticipant(false);
    setParticipantInstanceId(null);
  }, []);

  // Lock device - participant mode
  const lock = useCallback(() => {
    localStorage.setItem('kuutio-device-locked', 'true');
    setIsLocked(true);
  }, []);

  // Unlock with PIN
  const unlock = useCallback(async (pin) => {
    try {
      const response = await fetch(API_ENDPOINTS.AUTH_PIN_LOGIN(), {
        credentials: 'include',
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pinCode: pin }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        markPinSession();
        localStorage.removeItem('kuutio-device-locked');
        setIsLocked(false);
        return true;
      }
      return false;
    } catch (err) {
      console.error('[AuthGate] Unlock error:', err);
      return false;
    }
  }, []);

  // Auth model:
  //   Public routes — sign-in pages, the public report, and pages that check
  //     their own token or login (/me, /liity).
  //   Workshop views — every view a workshop uses; an instance PIN or a Sitra
  //     sign-in that reaches the link's instance. No workshop view opens
  //     without one (#755, #1116).
  //   Admin pages — an Entra session with a role.
  //   Anything else — an Entra session.
  const location = useLocation();
  const matches = (paths) => paths.some(p => location.pathname === p || location.pathname.startsWith(p + '/'));
  // Exact: '/screen' is the PIN form, '/screen/:code' is a wall screen.
  const publicExact = ['/', '/screen', '/pin', '/login', '/landing'];
  const publicPaths = [
    // The public report; logged-in users get more on the same page
    '/raportti', '/report',
    // A published artifact (specs/kuutio-agents/artifacts-microsite.md)
    '/artifact',
    // The participant's own page and its join link: they draw their own door (specs/me-account.md)
    '/me', '/liity',
    // The house wall and its phone page (specs/info-wall.md)
    '/info',
  ];
  const isPublicRoute = publicExact.includes(location.pathname) || matches(publicPaths);
  const workshopPaths = [
    // Wall screens and the views they render inline (/screen/A1, /screen/data, …)
    '/screen', '/slides',
    '/impactpath', '/impactmap', '/systemicmap', '/systeemikartta', '/pain-points',
    '/pivotpoints', '/pivotpoints-v1', '/pivotpoints-v2', '/lenses', '/swot', '/ratkaisut', '/kipupisteet', '/homework',
    '/tunnelma', '/postit', '/postit-ai', '/impact-machine',
    '/ongelmat', '/muutoksen-esteet', '/juurisyyt-v2', '/ongelmakartta', '/ratkaisut-v2',
    '/tulevaisuustestaus',
    // The same modules' paths before #1246 and #1336; they redirect to the ones above.
    '/kipupisteet-v2', '/systeemikartta-v2', '/kuviteltu-tulevaisuus', '/systeemikartta-v2-map',
    '/kuusi-kenttaa', '/kuusi-kenttaa/tehtava',
    '/vipupisteiden-valinta',
    '/vipupisteet-yhteiset', '/vipupisteet-yhteiset/tehtava',
    '/tilannekuva',
    '/welcome', '/kuutio', '/testikuutio', '/solutions',
    '/ennakointi',
    '/table', '/tablet', '/control', '/comments', '/weather',
  ];
  const needsWorkshopAuth = !isPublicRoute && matches(workshopPaths);
  // Read at render, not from state: the PIN form on / logs in and navigates
  // inside the app, so state set when AuthGate mounted would miss it.
  const hasPinSession = isPinSessionValid();
  // Admin pages need a role, or the page's action on the selected instance
  // (the table lives in AdminHeader/adminNav.js; specs/access-model.md).
  const requiredRole = getRequiredRole(location.pathname);
  // The instance the link names, if any; a workshop view takes it from ?kid or ?instance.
  const linkInstanceId = new URLSearchParams(location.search).get('instance')
    || new URLSearchParams(location.search).get('kid');
  const gateInstanceId = linkInstanceId || readRememberedInstanceId();
  const hasEntraSession = !!(me && me.email);

  // A PIN session the server no longer accepts → the PIN form. The browser
  // keeps a device "signed in" for four days on its own word; the session is
  // the server's. Asked when a workshop view opens and every few minutes
  // after, because a wall screen stays on the same page for days.
  const watchPinSession = needsWorkshopAuth && hasPinSession && me !== undefined && !hasEntraSession;
  useEffect(() => {
    if (!watchPinSession) return undefined;
    const check = async () => {
      if (await checkPinSession(API_ENDPOINTS.AUTH_PIN_VERIFY()) === 'stale') {
        clearPinSession();
        window.location.replace('/?denied=expired');
      }
    };
    check();
    const timer = setInterval(check, PIN_SESSION_CHECK_MS);
    return () => clearInterval(timer);
  }, [watchPinSession]);

  // Wait for /auth/me to resolve on any gated route — otherwise a signed-in
  // user would see a flash of the redirect while the request is in-flight.
  if (!isPublicRoute && me === undefined) return null;

  const redirectToEntra = () => {
    const returnTo = location.pathname + location.search;
    // Tell the backend which origin to land us back on — lets a dev backend
    // bounce a localhost frontend back to localhost (allowlisted server-side).
    const origin = encodeURIComponent(window.location.origin);
    window.location.href = `${API_ENDPOINTS.AUTH_ENTRA_LOGIN()}?return_to=${encodeURIComponent(returnTo)}&return_to_origin=${origin}`;
  };

  if (needsWorkshopAuth) {
    // No session, or one that does not reach the link's workshop → the front
    // page, which says why. The view is never drawn (#1116).
    const access = workshopAccess({
      me, hasPinSession, pinInstanceId: getPinInstanceId(), instanceId: linkInstanceId,
    });
    if (access !== 'ok') {
      window.location.replace(`/?denied=${access}`);
      return null;
    }
  } else if (requiredRole) {
    if (!hasEntraSession) {
      // A page a grant opens may be an outsider's (#799): /login offers them
      // an email code. A role-only page is staff's, straight to Entra.
      if (grantOpensPath(location.pathname)) {
        window.location.replace(`/login?return_to=${encodeURIComponent(location.pathname + location.search)}`);
      } else {
        redirectToEntra();
      }
      return null;
    }
    if (!mayOpenPath(me, location.pathname, gateInstanceId)) {
      // Signed in but lacks the access — don't re-trigger Entra (causes a
      // login loop, since the callback would redirect back here and we'd
      // land here again). A facilitator by role keeps their search; one by
      // grant goes to the Kuutio the grant opens, whichever it is — the
      // selected instance or the granted one (#793 testing: a fresh browser
      // has no remembered instance). Anyone else to their own page; the
      // /instances catalogue was the retired viewer role's landing.
      if (hasRole(me, 'facilitator')) {
        window.location.replace(`${FACILITATOR_HOME}${location.search}`);
      } else {
        const facilitated = canOnInstance(me, gateInstanceId, 'facilitate')
          ? gateInstanceId
          : Object.keys(me?.grants?.instance || {}).find((id) => canOnInstance(me, id, 'facilitate'));
        window.location.replace(facilitated ? `${FACILITATOR_HOME}?instance=${facilitated}` : '/me');
      }
      return null;
    }
  } else if (!isPublicRoute && !hasEntraSession) {
    // Any other gated route just needs *some* Entra session.
    redirectToEntra();
    return null;
  }

  // Authenticated (or public/admin-via-entra) → provide context and render children
  const contextValue = {
    isAuthenticated: isAuthenticated || hasRole(me, 'admin'),
    isLocked,
    // Admin means a signed-in role now; a PIN session is never one (#793).
    isAdmin: hasRole(me, 'admin'),
    isParticipant,
    participantInstanceId,
    user: me || null,
    login,
    logout,
    lock,
    unlock,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext };
export default AuthGate;
